import React, {
  CSSProperties,
  useState,
  useRef,
  useEffect,
  memo,
  useMemo,
} from 'react';
import { InputBasic } from 'components/common/Input';
import {
  find,
  propEq,
} from 'ramda';
import { Button } from '../Button';
import { ButtonsContainer } from '../ButtonsContainer';
import {
  Filter,
  FilterMatchingType,
} from 'interfaces/filters';
import {
  FiltersContainer,
  SectionHeader,
  SectionContent,
  StyledNumberCompare,
  ScrollContainer,
  StyledNoOptions,
} from './styled';
import { Dropdown } from '../Dropdown';
import {
  CompanyUserStatusOptions,
  COMPARE_OPTIONS,
  MetricAggregationMethodOptions,
  MetricOriginOptions,
  MetricCategoryOptions,
  RevenueTypeOptions,
  StatusOptions,
  TagTypeOptions,
  UserRoleOptions,
} from 'utils/constants';
import { useClickOutside } from 'hooks/useClickOutside';
import { Checkbox } from '../Checkbox';
import { NumberInput } from '../NumberInput';
import { Datepicker } from '../Datepicker';
import { useGetAllProductsQuery } from 'store/services/products';
import { useGetAllTagsQuery } from 'store/services/tags';
import { Subscriptions } from 'interfaces/subscriptions';
import { ContractLines } from 'interfaces/contractLines';
import { CustomersDropdownLazy } from '../CustomersDropdownLazy';

interface Props {
  isOpen: boolean;
  popperRef: React.Dispatch<React.SetStateAction<HTMLDivElement | null>>;
  filters: Array<Filter>;
  filtersDefs: Array<Filter>;
  data: Array<any>;
  setFilters: (filters: Array<Filter>) => void;
  clearAllFilters: () => void;
  onClose: () => void;
  style: CSSProperties;
  attributes: {
    [key: string]: {
      [key: string]: string;
    } | undefined;
  };
  subscriptionsOptions?: Subscriptions;
  contractLinesOptions?: ContractLines;
}

const RenderFilter = memo(({
  filter,
  innerState,
  setInnerState,
  options,
}: {
  filter: Filter;
  innerState: Filter[];
  setInnerState: React.Dispatch<React.SetStateAction<Filter[]>>;
  options: any[];
}) => {
  const {
    name,
    matching,
    key,
    label,
    optionNameField,
    optionValueField,
  } = filter;

  const distinctValues = filter.tagType
    ? options?.filter((ent) => ent.type === filter.tagType)
    : options
    || [];

  switch (matching) {
    case FilterMatchingType.STRING_INCLUDES:
      return (
        <InputBasic
          name={name}
          labelText=""
          value={find<Filter>(propEq('name', name))(innerState)?.value || ''}
          onChange={(ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const newState = innerState.map((filter: Filter) => filter.name === name
              ? { ...filter, value: ev.currentTarget.value }
              : filter,
            );
            setInnerState(newState);
          }}
        />
      );

    case FilterMatchingType.ARRAY_INCLUDES_PROPERTY:
      if (distinctValues.length === 0) {
        return (
          <StyledNoOptions>
            No {label.toLowerCase()} options
          </StyledNoOptions>
        );
      }

      return <>
        {distinctValues
          .map((val) => (
            <Checkbox
              key={val[optionNameField || key || name]}
              checked={find<Filter>(propEq('name', name))(innerState)?.value.includes(val[optionValueField || key || name])}
              name={val[optionNameField || key || name]}
              onChange={(checked: boolean) => {
                if (checked) {
                  const newState = innerState.map((filter: Filter) => filter.name === name
                    ? {
                      ...filter, value: [
                        ...filter.value,
                        val[optionValueField || key || name],
                      ],
                    }
                    : filter,
                  );
                  setInnerState(newState);
                } else {
                  const newState = innerState.map((filter: Filter) => filter.name === name
                    ? {
                      ...filter, value: [
                        ...filter.value.filter((item: string) => item !== val[optionValueField || key || name]),
                      ],
                    }
                    : filter,
                  );
                  setInnerState(newState);
                }
              }}
            >
              {val[optionNameField || key || name]}
            </Checkbox>
          ))}
      </>;

    case FilterMatchingType.ARRAY_INCLUDES_CUSTOMERS_DROPDOWN_LAZY:
      return (
        <CustomersDropdownLazy
          values={find<Filter>(propEq('name', name))(innerState)?.value.map((val: string) => ({ name: val, value: val }))}
          labelField={optionNameField || 'name'}
          valueField={optionValueField || 'name'}
          searchBy={optionNameField || 'name'}
          onChange={(val) => {
            const newState = innerState.map((filter: Filter) => filter.name === name
              ? {
                ...filter, value: val.map((ent) => ent.name),
              }
              : filter,
            );

            setInnerState(newState);
          }}
          multi
        />
      );

    case FilterMatchingType.ARRAY_INCLUDES_PROPERTY_DROPDOWN:
      if (distinctValues.length === 0) {
        return (
          <StyledNoOptions>
            No {label.toLowerCase()} options
          </StyledNoOptions>
        );
      }

      const preOptions = distinctValues
        .map((obj) => key ? obj[key] : obj);

      const options = [...new Set(preOptions)]
        .map((val) => ({ name: val, value: val }))
        .filter((opt) => opt.name);

      return (
        <Dropdown
          values={find<Filter>(propEq('name', name))(innerState)?.value.map((val: string) => ({ name: val, value: val }))}
          labelField="name"
          valueField="value"
          options={options}
          onChange={(val) => {
            const newState = innerState.map((filter: Filter) => filter.name === name
              ? {
                ...filter, value: val.map((ent) => ent.name),
              }
              : filter,
            );
            setInnerState(newState);
          }}
          multi
        />
      );

    case FilterMatchingType.NUMBER_COMPARE:
      return (
        <StyledNumberCompare>
          <Dropdown
            dropdownPosition="top"
            options={COMPARE_OPTIONS}
            onChange={(selectedMethod) => {
              const newState = innerState.map((filter: Filter) => filter.name === name
                ? { ...filter, value: { ...filter.value, compareMethod: selectedMethod[0].value } }
                : filter,
              );
              setInnerState(newState);
            }}
            values={
              [COMPARE_OPTIONS.find(
                (option) => option.value ===
                  find<Filter>(propEq('name', name))(innerState)?.value?.compareMethod || '',
              )]
            }
            labelField="name"
            dropdownHandle={false}
            searchable={false}
            oneSignType
          />
          <NumberInput
            name={name}
            labelText=""
            value={find<Filter>(propEq('name', name))(innerState)?.value?.value1 || ''}
            placeholder="Enter Amount"
            onChange={(value: string) => {
              const newState = innerState.map((filter: Filter) => filter.name === name
                ? { ...filter, value: { ...filter.value, value1: value } }
                : filter,
              );
              setInnerState(newState);
            }}
          />
        </StyledNumberCompare>
      );

    case FilterMatchingType.DATE_RANGE:
      return (
        <Datepicker
          name={name}
          labelText=""
          value={find<Filter>(propEq('name', name))(innerState)?.value || null}
          onChange={(date: Date | [Date | null, Date | null] | undefined) => {
            const newDate = Array.isArray(date) && date.every((d) => d === null) ? [] : date;
            const newState = innerState.map((filter: Filter) => filter.name === name
              ? { ...filter, value: newDate }
              : filter,
            );
            setInnerState(newState);
          }}
          selectsRange
        />
      );

    default:
      return null;
  }
});

export const Filters = memo(({
  isOpen,
  popperRef,
  filters,
  filtersDefs,
  data,
  setFilters,
  clearAllFilters,
  onClose,
  style,
  attributes,
  subscriptionsOptions,
  contractLinesOptions,
}: Props) => {
  const [innerState, setInnerState] = useState<Filter[]>(filters);
  const formRef = useRef<HTMLFormElement>(null);
  useClickOutside(formRef, onClose);

  const entities = filters.reduce((acc: string[], filter) => filter.options ? [...acc, filter.options] : acc, []);

  const options = {
    subscriptions: subscriptionsOptions
      ?.filter((ent) => !ent.deleted) || [],
    contractLines: contractLinesOptions
      ?.filter((ent) => !ent.deleted) || [],
    products: useGetAllProductsQuery(undefined, { skip: !isOpen || !entities.includes('products') })
      .data
      ?.filter((ent) => !ent.deleted) || [],
    tags: useGetAllTagsQuery(undefined, { skip: !isOpen || !entities.includes('tags') })
      .data
      ?.filter((ent) => !ent.deleted) || [],
    revenueTypeOptions: RevenueTypeOptions
      .map((option) => ({ ...option, id: option.label, revenueType: option.label })),
    statusOptions: StatusOptions
      .map((option) => ({ ...option, id: option.value, status: option.value })),
    tagTypeOptions: TagTypeOptions
      .map((option) => ({ ...option, id: option.label, type: option.label })),
    userRoleOptions: UserRoleOptions
      .map((option) => ({ ...option, id: option.value, role: option.value })),
    userStatusOptions: CompanyUserStatusOptions
      .map((option) => ({ ...option, id: option.value, status: option.value })),
    metricCategoryOptions: MetricCategoryOptions
      .map((option) => ({ ...option, id: option.value })),
    aggregationMethodOptions: MetricAggregationMethodOptions
      .map((option) => ({ ...option, id: option.value })),
    metricOriginOptions: MetricOriginOptions
      .map((option) => ({ ...option, id: option.value })),
  };

  const prefilteredData = useMemo(() => data?.filter((item) => !item.deleted), [data]);

  const handleClearAll = () => {
    clearAllFilters();
    setInnerState(filtersDefs);
    onClose();
  };

  const handleApply = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    setFilters(innerState);
    onClose();
  };

  const handleSubmit = (ev: React.FormEvent) => {
    ev.preventDefault();
  };

  useEffect(() => {
    setInnerState(filters);
    formRef.current?.focus();
  }, [filters]);

  return isOpen
    ? (
      <form
        ref={formRef}
        onSubmit={(ev) => handleSubmit(ev)}
      >
        <FiltersContainer
          ref={popperRef}
          style={style}
          {...attributes}
        >
          <ScrollContainer>
            {prefilteredData && filters.length && filters.map((filter: Filter) => (
              <div key={filter.name}>
                <SectionHeader>by {filter.label}</SectionHeader>
                <SectionContent maxHeight={filter.maxHeight}>
                  <RenderFilter
                    key={`filter_${filter.name}`}
                    filter={filter}
                    innerState={innerState}
                    setInnerState={setInnerState}
                    // @ts-ignore
                    options={filter.options ? options[filter.options] : []}
                  />
                </SectionContent>
              </div>
            ))}
          </ScrollContainer>
          <ButtonsContainer>
            <Button
              type="button"
              pushRight
              variant="borderless"
              onClick={handleClearAll}
            >
              Clear all
            </Button>
            <Button
              type="submit"
              onClick={handleApply}
            >
              Apply & Save
            </Button>
          </ButtonsContainer>
        </FiltersContainer>
      </form>
    )
    : null;
});
